/* The cart size-upgrade swap, and its undo.
 *
 * Turns one configured portrait line into the 18x24 of the same Format, carrying the customer's
 * uploaded photo and every other choice across so nothing has to be filled in again. The nudge
 * markup is snippets/ts-size-upsell.liquid; this file only acts on it.
 *
 * ── SHOPIFY CANNOT CHANGE A LINE'S VARIANT ─────────────────────────────────────────────────────
 *
 * /cart/change.js edits quantity and properties, never the variant. So an "upgrade" is an add plus
 * a remove, and the ORDER OF THOSE TWO CALLS IS LOAD-BEARING.
 *
 * ADD FIRST. The customer's photo lives at the URL in properties[Pet photo], uploaded once at
 * add-to-cart. Adding first means that URL is referenced by a live cart line for the whole
 * operation. Removing first would leave it unreferenced in the window between the calls, and a file
 * collected in that window fails at FULFILMENT — long after the customer has paid, with no way to
 * ask them for the photo again.
 *
 * The undo below is the same operation backwards, and it keeps the same rule for the same reason:
 * restore the original line first, drop the 18x24 second.
 *
 * ── THE PRINT-FILE TOKEN IS MINTED FRESH, AND THAT IS NOT COSMETIC ─────────────────────────────
 *
 * properties[_PrintFile] is `<origin>/print/<uuid>.jpg`, and studio/lib/shopify.ts validates it as
 * a SECURITY CONTROL: the origin must match PUBLIC_BASE_URL and the path must be exactly that
 * shape, because the uuid becomes a storage key the finished artwork is written to. So the origin
 * is preserved by rewriting only the uuid segment — never rebuilt from a base this file does not
 * have and should not know.
 *
 * The uuid must be NEW. Reusing it would leave two lines sharing one storage key in the window
 * between the add and the remove, and if the remove then failed, those two lines are DIFFERENT
 * SIZES — one would print at the wrong size, silently, with the order looking fine. A stranded
 * duplicate line is a failure the customer can see and fix. That one is not.
 *
 * ⚠ THE UNDO RE-MINTS TOO. Restoring the original size by copying the 18x24 line's token back
 * would hand the restored line a uuid that a removed line already owned, which is the same shared
 * key in a different disguise. Every add this file makes gets a fresh uuid, in both directions.
 *
 * ── _Preview IS DISPLAY ONLY ───────────────────────────────────────────────────────────────────
 *
 * Its only consumer is snippets/ts-cart-thumb.liquid, which rebuilds it through asset_url and
 * carries an onerror fallback to the product image. So substituting the size token is safe even
 * when the target rendition does not exist: the thumbnail quietly falls back. Fulfilment never
 * reads it.
 *
 * ── WHERE THE UNDO LIVES BETWEEN PAGE LOADS ────────────────────────────────────────────────────
 *
 * sessionStorage, because the swap can end in a full page reload (see render()) and an undo that
 * only exists in a closure would not survive it. The record is deliberately tiny — the upgraded
 * line's key, the line it came from, the two sizes and the variant to restore — and holds NO
 * customer data: the properties to restore are read back off the live cart line at undo time,
 * which is both smaller and always in step with whatever is actually in the cart now.
 */
(function () {
  /* German, and any later language, arrives as window.TS_I18N: snippets/ts-i18n.liquid prints it on
     every page that is NOT in the store's first language. English keeps the sentences written below,
     so an English visitor runs exactly the strings this file always ran. */
  var I18N = window.TS_I18N || {};
  function tr(key, en, vars) {
    var s = I18N[key] || en;
    for (var k in vars || {}) s = s.split('{' + k + '}').join(vars[k]);
    return s;
  }
  function sizeLabel(s) { return (I18N.sizes && I18N.sizes[s]) || s; }
  if (window.tsCartUpsellReady) return;
  window.tsCartUpsellReady = true;

  var UNDO_KEY = 'tsUpsellUndo';

  /* ⚠ PUB_SUB_EVENTS IS NOT ON window, AND EVERY GUARD IN THIS THEME THAT TESTED window.PUB_SUB_EVENTS
     WAS PERMANENTLY FALSE. constants.js:3 declares it `const PUB_SUB_EVENTS = {...}` at the top level
     of a classic script, and a top-level const goes into the global LEXICAL environment: reachable as
     a bare identifier from any other classic script, never as a property of window. Measured on the
     live page — window.PUB_SUB_EVENTS is undefined while bare PUB_SUB_EVENTS.cartUpdate is
     "cart-update". So the two publishes below and the subscription at the bottom of this file had
     silently never run, and nothing outside this file ever learned that an upgrade changed the cart.

     typeof is the only safe test: a bare reference to a genuinely undeclared identifier throws
     ReferenceError rather than returning undefined. */
  function pubSub() {
    try { return (typeof PUB_SUB_EVENTS !== 'undefined') ? PUB_SUB_EVENTS : null; } catch (_) { return null; }
  }
  function publishCartUpdate(state) {
    var E = pubSub();
    if (typeof publish === 'function' && E && E.cartUpdate) {
      publish(E.cartUpdate, { source: 'ts-cart-upsell', cartData: state });
    }
  }

  /* Copied from sections/ts-product.liquid rather than shared, because that file is section-scoped
     and the drawer is global. studio/scripts/test-theme-contract.ts exercises BOTH copies against
     each crypto fallback — keep them in step. A weak uuid loses one order to a collision; an empty
     one loses every order placed while the tab is open. */
  function uuidv4() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (_) {}
    try {
      if (window.crypto && crypto.getRandomValues) {
        var b = crypto.getRandomValues(new Uint8Array(16));
        b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
        var h = []; for (var i = 0; i < 16; i++) h.push((b[i] + 0x100).toString(16).slice(1));
        return h[0]+h[1]+h[2]+h[3]+'-'+h[4]+h[5]+'-'+h[6]+h[7]+'-'+h[8]+h[9]+'-'+h[10]+h[11]+h[12]+h[13]+h[14]+h[15];
      }
    } catch (_) {}
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0; return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    });
  }

  /* Swap the uuid, keep everything else. Anything that is not our expected shape is passed through
     untouched: a line whose token we do not recognise is one the Studio will flag for an operator,
     which is a better outcome than this file inventing a URL for it. */
  function remintPrintFile(url) {
    if (!url) return url;
    return String(url).replace(/\/print\/[^/]+\.jpg(\?.*)?$/, '/print/' + uuidv4() + '.jpg');
  }

  /* The size token sits between separators in every mockup filename, so it is bounded on both sides
     rather than replaced loosely — "8x10" appears nowhere else in these names, but a bare replace
     would still be a latent bug the first time it did. */
  function remapPreview(url, fromSize, toSize) {
    if (!url || !fromSize || !toSize) return url;
    return String(url).replace(new RegExp('-' + fromSize + '(?=[-.])', 'g'), '-' + toSize);
  }

  /* ── THE NAME BAND DOES NOT SURVIVE A SIZE SWAP OF A -scale RENDER ────────────────────────────
     _PreviewName is where the product page painted the name, as fractions of the card it painted it
     on (stampPreviewName in ts-product.liquid). The hero composite is one render served under every
     size, so its band is the same at 18x24 as at 8x10 and rides along. A "-scale" wall render is per
     size — the frame opening moves — so a band read off the 8x10 one is wrong on the 18x24 one.
     Dropped rather than remapped: the studio's as-sold tile then shows the card without the name
     and says so, which beats showing the name where it never was. */
  function remapProps(props, fromSize, toSize) {
    if (!props._Preview) return;
    if (/-scale(?=[-.])/.test(String(props._Preview))) delete props._PreviewName;
    props._Preview = remapPreview(props._Preview, fromSize, toSize);
  }

  function cartFetch(path, body) {
    return fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.text().then(function (t) {
        var parsed = null;
        try { parsed = JSON.parse(t); } catch (_) {}
        if (!r.ok) throw new Error((parsed && (parsed.description || parsed.message)) || tr('cart_request_failed', 'Cart request failed'));
        return parsed;
      });
    });
  }

  function getCart() {
    return fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function findLine(cart, key) {
    if (!cart || !cart.items || !key) return null;
    for (var i = 0; i < cart.items.length; i++) if (cart.items[i].key === key) return cart.items[i];
    return null;
  }

  /* The freshly minted token is unique to the line we just added, which makes it the one identifier
     that cannot match anything else in the cart — including another line of the same variant and
     the same photo. */
  function findByPrintFile(cart, printFile) {
    if (!cart || !cart.items || !printFile) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if ((cart.items[i].properties || {})._PrintFile === printFile) return cart.items[i];
    }
    return null;
  }

  function copyProps(line) {
    var props = {};
    Object.keys((line && line.properties) || {}).forEach(function (k) { props[k] = line.properties[k]; });
    return props;
  }

  // ── the undo record ────────────────────────────────────────────────────────────────────────
  // Every read and write is wrapped: Safari's private mode throws on sessionStorage rather than
  // returning null, and losing the undo is survivable where losing the upgrade is not.
  function readUndo() {
    try { return JSON.parse(sessionStorage.getItem(UNDO_KEY) || 'null'); } catch (_) { return null; }
  }
  function writeUndo(rec) {
    try { sessionStorage.setItem(UNDO_KEY, JSON.stringify(rec)); } catch (_) {}
  }
  function clearUndo() {
    try { sessionStorage.removeItem(UNDO_KEY); } catch (_) {}
  }

  /* ── dismissed offers ─────────────────────────────────────────────────────────────────────────
     A list of line keys the customer has closed the offer on. Same storage and the same wrapping as
     the undo above, for the same reasons: a swap can end in a full page reload, so a closure would
     not survive it, and Safari private mode throws on access rather than returning null.

     SESSION, not local, and that is the whole judgement here. A dismissal that outlived the visit
     would permanently silence the offer on the highest-contribution variant after one idle click,
     on a customer who might well want it next time. Gone for this visit, back on the next. */
  var DISMISS_KEY = 'tsUpsellDismissed';
  function readDismissed() {
    try {
      var v = JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]');
      return Array.isArray(v) ? v : [];
    } catch (_) { return []; }
  }
  function addDismissed(key) {
    if (!key) return;
    var list = readDismissed();
    if (list.indexOf(key) === -1) list.push(key);
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(list)); } catch (_) {}
  }

  /* Hide the offers the customer has closed, and only those — keyed on the line, so dismissing one
     line leaves an eligible sibling showing. Runs wherever paintUndo() runs, plus on cartUpdate. */
  function paintDismissed() {
    var list = readDismissed();
    if (!list.length) return;
    var offers = document.querySelectorAll('[data-ts-upsell]');
    for (var i = 0; i < offers.length; i++) {
      var el = offers[i];
      if (list.indexOf(el.getAttribute('data-line-key')) !== -1) el.hidden = true;
    }
  }

  /* Reveal the undo on the line the upgrade produced, and only that line. The mount point is
     rendered by ts-size-upsell.liquid on every 18x24 wall-art line and starts hidden, so a cart
     that was already 18x24 before this tab ever ran shows nothing. */
  function paintUndo() {
    var rec = readUndo();
    var mounts = document.querySelectorAll('[data-ts-undo]');
    for (var i = 0; i < mounts.length; i++) {
      var el = mounts[i];
      var mine = !!rec && el.getAttribute('data-line-key') === rec.key;
      el.hidden = !mine;
      if (!mine) continue;
      var sub = el.querySelector('.ts-undo-sub');
      if (sub) sub.textContent = tr('upsell_upgraded_from', 'Upgraded from {from}.', { from: sizeLabel(rec.fromSize) });
      var btn = el.querySelector('.ts-upsell-go');
      if (btn && !btn.dataset.label) btn.dataset.label = btn.textContent.trim();
    }
  }

  function busy(root, btn, msg, label) {
    if (root.dataset.busy) return false;
    root.dataset.busy = '1';
    if (btn) { btn.disabled = true; btn.textContent = label; }
    if (msg) msg.textContent = '';
    return true;
  }

  function release(root, btn, msg, text) {
    root.dataset.busy = '';
    if (btn) { btn.disabled = false; btn.textContent = btn.dataset.label || tr('upsell_upgrade', 'Upgrade'); }
    if (msg) msg.textContent = text;
  }

  function swap(root) {
    var key = root.getAttribute('data-line-key');
    var toVariant = Number(root.getAttribute('data-to-variant'));
    var fromVariant = Number(root.getAttribute('data-from-variant'));
    var fromSize = root.getAttribute('data-from-size');
    var toSize = root.getAttribute('data-to-size');
    var btn = root.querySelector('.ts-upsell-go');
    var msg = root.querySelector('.ts-upsell-msg');

    if (!busy(root, btn, msg, tr('upsell_upgrading', 'Upgrading…'))) return;
    var minted = null;

    /* Read the live cart rather than trusting the rendered markup: the customer may have changed
       the quantity since this nudge was painted, and the upgraded line has to match what is
       actually there. */
    getCart()
      .then(function (cart) {
        var line = findLine(cart, key);
        if (!line) throw new Error(tr('upsell_line_gone', 'That item is no longer in your cart.'));

        var props = copyProps(line);
        if (props._PrintFile) props._PrintFile = remintPrintFile(props._PrintFile);
        remapProps(props, fromSize, toSize);
        minted = props._PrintFile || null;

        return cartFetch(window.routes.cart_add_url + '.js', {
          items: [{ id: toVariant, quantity: line.quantity, properties: props }],
        }).then(function () {
          /* Record the undo BEFORE the remove, not after. If the remove fails, this is what makes
             the stranded 18x24 recoverable even if the customer reloads instead of clicking the
             button we are about to show them. Its own failure is swallowed: the upgrade is what
             was asked for, and losing the safety net must not cost the customer the thing itself. */
          return getCart().then(function (afterAdd) {
            var added = findByPrintFile(afterAdd, minted);
            if (added) writeUndo({ key: added.key, originalKey: key, fromVariant: fromVariant, fromSize: fromSize, toSize: toSize });
          }).catch(function () {}).then(function () {
            /* Ask for the rendered sections back on the REMOVE, exactly as cart.js does on a
               quantity change. A second standalone fetch cannot do this job: the Section Rendering
               API keys on the template's own section ids (`template--…__cart-items`), not on the
               DOM ids Dawn hangs off them, and those ids are only readable from the live DOM.
               Reading them here, before the re-render replaces the nodes, is the same reason
               cart.js caches them before its fetch. */
            var targets = sectionTargets();
            var body = { id: key, quantity: 0 };
            if (targets.length) { body.sections = targets; body.sections_url = window.location.pathname; }
            return cartFetch(window.routes.cart_change_url + '.js', body);
          });
        });
      })
      .then(function (state) { render(state); })
      .catch(function (e) {
        /* Two different failures wearing one message until now. If the add never landed the cart is
           untouched and "try again" is the whole story. If it landed and the REMOVE failed, the
           customer is holding two lines and needs to be told that, and handed the way out. */
        if (minted) {
          /* The offer stays DISABLED here, unlike every other failure. The add landed, so clicking
             Upgrade again would add a second 18x24 on top of the stranded one and make the cart
             worse in exactly the way the customer is already trying to get out of. The way
             forward is the recovery button; the offer comes back with the line it belongs to. */
          root.dataset.busy = '';
          if (btn) { btn.disabled = true; btn.textContent = btn.dataset.label || tr('upsell_upgrade', 'Upgrade'); }
          if (msg) msg.textContent = tr('upsell_stranded', 'Added the {to}, but the {from} is still in your cart.', { to: sizeLabel(toSize), from: sizeLabel(fromSize) });
          offerRecover(root, msg, toSize);
        } else {
          release(root, btn, msg, e.message || tr('upsell_failed', 'Could not upgrade. Please try again.'));
        }
      });
  }

  /* The stranded-swap way out, shown on the nudge the customer just clicked, because that is where
     they are looking. It reverts rather than retries: back to one line of the size they started
     with is a cart they recognise, and the offer is still there if they want to try again. */
  function offerRecover(root, msg, toSize) {
    if (root.querySelector('.ts-upsell-recover')) return;
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ts-upsell-go ts-upsell-recover';
    b.textContent = tr('upsell_remove', 'Remove the {to}', { to: sizeLabel(toSize) });
    b.dataset.label = b.textContent;
    (msg && msg.parentNode ? msg.parentNode : root).insertBefore(b, msg ? msg.nextSibling : null);
  }

  /* One reversal for both endings, because at this point they differ only in what is in the cart,
     and that is read rather than assumed:
       · the ORIGINAL LINE IS STILL THERE — the remove failed, so undoing is just dropping the
         duplicate 18x24;
       · it is GONE — the swap completed, so the original has to be put back before the 18x24 goes.
     Add first, remove second, for the same reason the upgrade does it in that order. */
  function revert(root, btn, msg) {
    var rec = readUndo();
    if (!rec) { release(root, btn, msg, tr('upsell_nothing_to_undo', 'There is nothing to undo.')); return; }

    getCart()
      .then(function (cart) {
        var upgraded = findLine(cart, rec.key);
        if (!upgraded) {
          // Cleared, but NOT repainted: hiding the bar here would take the explanation down with
          // it. The next cart render drops the mount along with the line it belonged to.
          clearUndo();
          throw new Error(tr('upsell_line_gone', 'That item is no longer in your cart.'));
        }

        var targets = sectionTargets();
        var drop = { id: rec.key, quantity: 0 };
        if (targets.length) { drop.sections = targets; drop.sections_url = window.location.pathname; }

        if (findLine(cart, rec.originalKey)) {
          return cartFetch(window.routes.cart_change_url + '.js', drop);
        }

        var props = copyProps(upgraded);
        if (props._PrintFile) props._PrintFile = remintPrintFile(props._PrintFile);
        remapProps(props, rec.toSize, rec.fromSize);
        var restoredToken = props._PrintFile || null;

        return cartFetch(window.routes.cart_add_url + '.js', {
          items: [{ id: rec.fromVariant, quantity: upgraded.quantity, properties: props }],
        }).then(function () {
          /* Re-point the record at the line just restored, BEFORE the remove — the same reason the
             upgrade records before its own remove. Without this, an undo whose remove failed would
             leave a record still naming a key that no longer exists, and the retry would read that
             as "nothing restored yet" and restore a SECOND copy. */
          return getCart().then(function (afterAdd) {
            var restored = findByPrintFile(afterAdd, restoredToken);
            if (restored) writeUndo({ key: rec.key, originalKey: restored.key, fromVariant: rec.fromVariant,
                                      fromSize: rec.fromSize, toSize: rec.toSize });
          }).catch(function () {});
        }).then(function () {
          return cartFetch(window.routes.cart_change_url + '.js', drop);
        });
      })
      .then(function (state) { clearUndo(); render(state); })
      .catch(function (e) { release(root, btn, msg, e.message || tr('upsell_undo_failed', 'Could not undo. Please try again.')); });
  }

  /* Which sections to ask for. The drawer is a STANDALONE section (`cart-drawer`), so the Section
     Rendering API resolves it by name. The cart page's carry their own template-scoped ids, read
     from the DOM here while the nodes still exist — see pageTargets(). */
  function sectionTargets() {
    var page = pageTargets();
    if (page.length) return page.map(function (t) { return t.section; });
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  /* The cart page's replacement plan, in Dawn's own shape and order: which section answers for
     which element, and which node inside it gets replaced. `main-cart-items` and `main-cart-footer`
     are TEMPLATE-SCOPED (`template--<id>__cart-items`) and their ids live only in the rendered
     DOM's data-id, so they are read before the re-render replaces those nodes. */
  function pageTargets() {
    var items = document.getElementById('main-cart-items');
    if (!items || !items.dataset.id) return [];
    if (drawerIsOpen()) return [];
    var footer = document.getElementById('main-cart-footer');
    var want = [
      { id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents', scoped: true },
      { id: 'main-cart-footer', section: footer && footer.dataset.id, selector: '.js-contents', scoped: true },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section', scoped: false },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section', scoped: false },
    ];
    return want.filter(function (t) { return t.section && document.getElementById(t.id); });
  }

  /* The drawer markup is rendered on EVERY template including /cart — layout/theme.liquid renders
     it unconditionally when cart_type is drawer — so "is #main-cart-items present" is not on its
     own the question of which surface the customer is looking at. */
  function drawerIsOpen() {
    var d = document.querySelector('cart-drawer');
    return !!(d && d.classList.contains('active'));
  }

  function sectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  /* ── WHY THE CART PAGE TRIES, AND ONLY THEN RELOADS ──────────────────────────────────────────
     The drawer re-renders in place: `cart-drawer` is a standalone section, the API returns its
     markup, and renderContents already knows how to swap the panel and the badge together.
     The cart page's sections are TEMPLATE-SCOPED, and `theme dev` answers those with `null` — not
     our bug and not Dawn's, as watching Dawn's own quantity change on the same page shows: it asks
     for the same ids, gets `template--…__cart-items -> null` back beside a perfectly rendered
     `cart-icon-bubble`, and leaves its own free-shipping bar stale as a result.
     The LIVE store renders them correctly. So this used to reload the page unconditionally, which
     was right against the dev server and wasted a full page load on every real upgrade a customer
     made. It now DETECTS the null instead of assuming it: ask, and reload only if what came back
     cannot be applied. Expect a reload under `theme dev` and an in-place swap on live.

     The gate is the TEMPLATE-SCOPED sections only. Those carry the cart itself, so a null there
     means there is nothing to paint. The standalone two are applied when present and skipped when
     not: reloading the whole page because an aria-live region did not come back would trade the
     saving for nothing. A reload is always correct, which a half-applied cart would not be, so
     anything unexpected still ends there. */
  function render(state) {
    var page = pageTargets();
    if (page.length) { renderPage(state, page); return; }

    /* An open drawer standing in front of the cart page is the one combination that cannot be
       re-rendered correctly: the two surfaces need different sections and only one set can be
       asked for, so whichever is painted leaves the other stale. It keeps the unconditional
       reload, which is always right and costs nothing that was not already being spent here. */
    if (document.getElementById('main-cart-items')) { window.location.reload(); return; }

    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.renderContents) { window.location.reload(); return; }
    try {
      if (!state || !state.sections) { window.location.reload(); return; }
      drawer.renderContents(state);
      publishCartUpdate(state);
      paintUndo(); paintDismissed();
    } catch (_) {
      window.location.reload();
    }
  }

  function renderPage(state, targets) {
    var usable = function (t) {
      var html = state && state.sections && state.sections[t.section];
      return typeof html === 'string' && html.length > 0;
    };
    for (var i = 0; i < targets.length; i++) {
      if (targets[i].scoped && !usable(targets[i])) { window.location.reload(); return; }
    }
    try {
      targets.forEach(function (t) {
        if (!usable(t)) return;
        var host = document.getElementById(t.id);
        (host.querySelector(t.selector) || host).innerHTML = sectionInnerHTML(state.sections[t.section], t.selector);
      });
      // Dawn's own emptiness toggles, which the re-rendered markup does not carry itself.
      var empty = state.item_count === 0;
      var items = document.getElementById('main-cart-items');
      var footer = document.getElementById('main-cart-footer');
      if (items) items.classList.toggle('is-empty', empty);
      if (footer) footer.classList.toggle('is-empty', empty);
      publishCartUpdate(state);
      paintUndo(); paintDismissed();
    } catch (_) {
      window.location.reload();
    }
  }

  /* Delegated, because every one of these nodes is replaced wholesale on each cart re-render —
     binding per button would go dead the first time the customer changed a quantity. */
  /* The dismiss, on the same delegated listener and for the same reason the comment below gives:
     every one of these nodes is replaced wholesale on each cart re-render, so a per-button binding
     would go dead the first time the customer changed a quantity. */
  /* ── REMOVING A PORTRAIT TAKES ITS $19 ADD-ON WITH IT ─────────────────────────────────────────
     The add-on buys the files for ONE print, joined to it by `_ForPrint`. Left behind on its own it
     is money taken for files with no artwork to build: the Studio's ingest can spot the orphan and
     shout, but only after the customer has paid, and only a human can unpick it. Far better that it
     never reaches checkout.

     CAPTURE PHASE, and it stops the event. Dawn's <cart-remove-button> binds its own click and would
     otherwise fire a second, competing /cart/change.js for the same line — two cart writes racing to
     re-render the same panel. This does both removals in ONE /cart/update.js instead, which takes a
     map of line keys and is therefore atomic: the pair cannot half-succeed.

     The pairing is decided in Liquid and stamped on the button (see ts-cart-drawer.liquid), so the
     decision to intercept is synchronous. Without a `data-addon-key` this listener does nothing at
     all and every ordinary removal keeps Dawn's own path. */
  document.addEventListener('click', function (e) {
    var host = e.target.closest && e.target.closest('cart-remove-button[data-addon-key]');
    if (!host) return;
    var lineKey = host.getAttribute('data-line-key');
    var addonKey = host.getAttribute('data-addon-key');
    if (!lineKey || !addonKey) return;                 // nothing to pair: let Dawn have it

    e.preventDefault();
    e.stopPropagation();

    var updates = {};
    updates[lineKey] = 0;
    updates[addonKey] = 0;
    cartFetch(window.routes.cart_update_url + '.js', {
      updates: updates,
      sections: sectionTargets(),
      sections_url: window.location.pathname,
    })
      .then(render)
      /* A failed removal is the safe direction — the customer still has both lines and can try
         again — so this reloads rather than leaving a panel that disagrees with the cart. */
      .catch(function () { window.location.reload(); });
  }, true);

  document.addEventListener('click', function (e) {
    var x = e.target.closest && e.target.closest('[data-ts-upsell-dismiss]');
    if (!x) return;
    var root = x.closest('[data-ts-upsell]');
    if (!root) return;
    e.preventDefault();
    addDismissed(root.getAttribute('data-line-key'));
    root.hidden = true;
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.ts-upsell-go');
    if (!btn) return;
    if (!btn.dataset.label) btn.dataset.label = btn.textContent.trim();

    var undoRoot = btn.closest('[data-ts-undo]');
    if (undoRoot) {
      e.preventDefault();
      var uMsg = undoRoot.querySelector('.ts-upsell-msg');
      if (!busy(undoRoot, btn, uMsg, tr('upsell_undoing', 'Undoing…'))) return;
      revert(undoRoot, btn, uMsg);
      return;
    }

    var root = btn.closest('[data-ts-upsell]');
    if (!root) return;
    e.preventDefault();

    if (btn.classList.contains('ts-upsell-recover')) {
      var rMsg = root.querySelector('.ts-upsell-msg');
      if (!busy(root, btn, rMsg, tr('upsell_removing', 'Removing…'))) return;
      revert(root, btn, rMsg);
      return;
    }
    swap(root);
  });

  function repaint() { paintUndo(); paintDismissed(); }

  /* ⚠ THE cartUpdate SUBSCRIPTION IS WHAT MAKES EITHER OF THESE STICK. Both painters used to run
     only on load and inside this file's own render(). Dawn re-renders the drawer by a completely
     different route — cart.js onCartUpdate() does targetElement.replaceWith(sourceElement) on
     cart-drawer-items — which this file never hears about. So a plain quantity change rebuilt the
     markup from the server and threw away whatever had been painted onto it.

     For the dismissal that is fatal: an offer that comes back on the next quantity change has not
     been dismissed. It was also silently breaking the UNDO, which should have vanished the same
     way; subscribing here fixes both, and the undo half was a pre-existing bug rather than
     something this change introduced. */
  try {
    var E = pubSub();
    if (typeof subscribe === 'function' && E && E.cartUpdate) {
      subscribe(E.cartUpdate, function () {
        /* After the re-render, not before: the new nodes have to exist to be painted. */
        setTimeout(repaint, 0);
      });
    }
  } catch (_) {}

  /* ⚠ AND A BELT TO GO WITH THOSE BRACES. cartUpdate is published by cart.js AFTER it repaints the
     sections inline, which the timeout above covers — but CartDrawerItems.onCartUpdate() ALSO
     re-fetches ?section_id=cart-drawer and replaceWith()s the whole node, on its own timeline, well
     after the event. Whatever the subscriber painted is thrown away when that lands.

     An observer is the only thing that survives every route into this markup, because it watches the
     result rather than trying to predict the path. Cheap: it fires on cart re-renders, not on
     anything a customer does with the page, and repaint() is two querySelectorAll calls that no-op
     when nothing is stored. */
  try {
    var drawerRoot = document.querySelector('cart-drawer');
    if (drawerRoot && window.MutationObserver) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        setTimeout(function () { pending = false; repaint(); }, 0);
      }).observe(drawerRoot, { childList: true, subtree: true });
    }
  } catch (_) {}

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', repaint);
  else repaint();
})();
